import { storeToRefs } from 'pinia';
import { useCartStore } from '../stores/cart';
import {
  AddCartItemsOptions,
  AddCartItemsResult,
  CartItemChangeResult,
  CartItemQuantitiesInput,
  CartItemQuantity,
  CartActionPlan,
} from '../types/models';
import { AddToCartItem, ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';
import { ShopifyCartRequestError, useShopifyCartService } from './shopifyCart';
import { enqueueCartMutation } from './cartMutationQueue';
import { getLineIdentity } from './lineItemProperties';
import {
  areRemovalsCurrent,
  fitsProjectedCart,
  getPreAddLineQuantities,
  getRestoreItems,
  groupStockLines,
  hasReleasedCapacity,
  planRemovalSwap,
  planStockSwap,
  sharesTrackedStock,
  type PendingStockSwap,
  type StockSwap,
} from './cartStockSwap';
import {
  getAddedQuantities,
  getFailedAddResult,
  getReducedLineQuantities,
  getRejectedAddResult,
  getUntargetedRemovals,
  isStockRejectionError,
  type CartMutationResult,
} from './cartMutationResults';
import { cartStockCorrection } from './cartStockCorrection';
import { cartUrlActionFlow } from './cartUrlActionFlow';
import { cartFreeGiftSwap } from './cartFreeGiftSwap';
import { useCartValidation } from '../composables/useCartValidation';
import { findVariantParentMaxQuantityFamilies } from '../cartRules/variantParentMaxQuantity';
import { useProductStockStore } from '@/features/Product/stores/productStock';

const CART_RESYNC_DELAY = 2000;
const MAX_CART_RESYNC_ATTEMPTS = 3;

let isCartStale = false;
let cartResyncAttempts = 0;
let cartResyncTimer: ReturnType<typeof setTimeout> | null = null;
let pendingStockSwap: PendingStockSwap | null = null;

export const cartActions = () => {
  const {
    DEFAULT_EMPTY_CART,
    updateCart,
    updateCartAttributes,
    changeCartItem,
    getCart,
    clearCart,
    addToCart,
  } = useShopifyCartService();
  const cartStore = useCartStore();
  const {
    setCart,
    addCartStockRemovals,
    setCartAddLimitedByStock,
    setCartAdjustedForStock,
    startCartAdd,
    setLatestAddLineIdentities,
    isFreeGiftItem,
    setChosenFreeGiftSku,
  } = cartStore;
  const {
    cartData,
    cartDataLoaded,
    latestAddLineIdentities,
    cartUrlNotice,
    cartNoticesVisible,
    freeGiftItem,
  } = storeToRefs(cartStore);
  const { validateCart } = useCartValidation();
  const productStockStore = useProductStockStore();
  const { refreshStock, getExactStock, checkAddStockWithRedraws, getStockFittedAddQuantity } =
    productStockStore;
  const { unavailableLines } = storeToRefs(productStockStore);
  const { trackStockCorrection, resolveStockCorrection, refreshStockAfterRejection } =
    cartStockCorrection();

  const scheduleCartResync = () => {
    if (cartResyncTimer || cartResyncAttempts >= MAX_CART_RESYNC_ATTEMPTS) return;
    cartResyncAttempts += 1;
    cartResyncTimer = setTimeout(() => {
      cartResyncTimer = null;
      refreshCart();
    }, CART_RESYNC_DELAY);
  };

  const settleStockSwap = async (cart: ShopifyCart): Promise<ShopifyCart> => {
    const settlement = pendingStockSwap;
    if (!settlement) return cart;
    const { swap, items, releasedCart, isAddSent, acceptedQuantities } = settlement;
    pendingStockSwap = null;
    const confirmedQuantities =
      acceptedQuantities ??
      (isAddSent && releasedCart
        ? getAddedQuantities(items, releasedCart, cart)
        : items.map(() => 0));
    trackStockCorrection(getPreAddLineQuantities(swap, confirmedQuantities));
    const restoreItems = getRestoreItems(swap, confirmedQuantities, cart);
    if (!restoreItems.length) return cart;
    await addToCart(restoreItems).catch((error: unknown) =>
      console.error('Failed to restore released cart items', error),
    );
    return getCart();
  };

  const dismissCartNotices = () => {
    if (!cartNoticesVisible.value) {
      urlActionFlow.releaseCartUrlAction();
      return;
    }
    urlActionFlow.dismissCartUrlNotice();
    setCartAdjustedForStock(false);
    setCartAddLimitedByStock(false);
  };

  const syncCart = async (): Promise<ShopifyCart | null> => {
    const cartUrlActionRecord = urlActionFlow.getUnresolvedCartUrlActionRecord();
    if (cartUrlActionRecord) {
      urlActionFlow.markCartUrlActionPending(
        cartUrlNotice.value ?? { type: 'unconfirmed', items: [] },
      );
    }
    try {
      const fetchedCart = await settleStockSwap(await getCart());
      if (cartUrlActionRecord) {
        urlActionFlow.resolveCartUrlActionRecord(cartUrlActionRecord, fetchedCart);
      }
      resolveStockCorrection(fetchedCart);
      setCart(await validateCart(fetchedCart));
      isCartStale = false;
      cartResyncAttempts = 0;
      return fetchedCart;
    } catch (error) {
      console.error('Failed to sync cart', error);
      if (!cartDataLoaded.value) setCart(DEFAULT_EMPTY_CART());
      isCartStale = true;
      scheduleCartResync();
      return null;
    }
  };

  const prepareCartForMutation = async (): Promise<boolean> => {
    if (!cartDataLoaded.value || isCartStale || pendingStockSwap) await syncCart();
    return !pendingStockSwap;
  };

  const mutateAndSync = async (
    mutate: () => Promise<ShopifyCart | void>,
    isTargeted: (item: ShopifyCartItem) => boolean,
  ): Promise<CartMutationResult> => {
    const cartBefore = isCartStale ? null : cartData.value;
    const { error, responseCart } = await mutate().then(
      (cart) => ({ error: null, responseCart: cart ?? null }),
      (mutationError: unknown) => ({ error: mutationError, responseCart: null }),
    );
    const fetchedCart = await syncCart();
    if (!fetchedCart && responseCart) resolveStockCorrection(responseCart);
    const result = { error, cartBefore, fetchedCart };
    if (!cartBefore || !fetchedCart) return result;
    const removals = getUntargetedRemovals(cartBefore, fetchedCart, isTargeted);
    if (removals.length) addCartStockRemovals(removals);
    return result;
  };

  const urlActionFlow = cartUrlActionFlow({
    syncCart,
    mutateAndSync,
    hasPendingStockSwap: () => !!pendingStockSwap,
  });

  const { selectFreeGift } = cartFreeGiftSwap({
    dismissCartNotices,
    prepareCartForMutation,
    mutateAndSync,
  });

  const performCartMutation = async (
    mutate: () => Promise<ShopifyCart | void>,
    isTargeted: (item: ShopifyCartItem) => boolean,
  ): Promise<CartMutationResult> => {
    if (!(await prepareCartForMutation())) {
      return {
        error: new Error('A previous add is still being settled'),
        cartBefore: null,
        fetchedCart: null,
      };
    }
    return mutateAndSync(mutate, isTargeted);
  };

  const runCartMutation = (
    mutate: () => Promise<ShopifyCart | void>,
    isTargeted: (item: ShopifyCartItem) => boolean,
  ): Promise<CartMutationResult> =>
    enqueueCartMutation(() => performCartMutation(mutate, isTargeted));

  const refreshCart = (): Promise<ShopifyCart> =>
    enqueueCartMutation(async () => {
      await syncCart();
      return cartData.value;
    });

  const prepareStockSwap = (items: AddToCartItem[], cart: ShopifyCart): StockSwap | null => {
    const getStockLines = () =>
      groupStockLines(items, cart, {
        isFreeGiftItem,
        unavailableLineKeys: new Set(unavailableLines.value),
        protectedLineIdentities: new Set(latestAddLineIdentities.value),
      });
    const isExactStock = (variantId: number) => getExactStock(variantId) !== undefined;
    if (!sharesTrackedStock(getStockLines(), isExactStock)) return null;
    return planStockSwap(getStockLines(), getExactStock);
  };

  const runStockSwap = async (
    items: AddToCartItem[],
    swap: StockSwap,
  ): Promise<AddCartItemsResult> => {
    const releaseUpdates = Object.fromEntries(
      swap.releases.map(({ key, quantity }) => [key, quantity]),
    );
    const release = await updateCart(releaseUpdates).then(
      (cart) => ({ cart, isRejected: false }),
      (error: unknown) => {
        console.error('Failed to release cart items for a new add', error);
        return { cart: null, isRejected: error instanceof ShopifyCartRequestError };
      },
    );
    if (release.isRejected) {
      await syncCart();
      return getRejectedAddResult(items, false);
    }
    const settlement: PendingStockSwap = {
      swap,
      items,
      releasedCart: null,
      isAddSent: false,
      acceptedQuantities: null,
    };
    pendingStockSwap = settlement;
    const releasedCart = (await getCart().catch(() => null)) ?? release.cart;
    if (!releasedCart) {
      await syncCart();
      return getRejectedAddResult(items, false);
    }
    if (!hasReleasedCapacity(swap, releasedCart)) {
      refreshStock({ force: true });
      if (!(await syncCart()) && pendingStockSwap === settlement) {
        await settleStockSwap(releasedCart).catch((error: unknown) =>
          console.error('Failed to confirm restored cart items', error),
        );
      }
      return getRejectedAddResult(items, true);
    }
    settlement.releasedCart = releasedCart;
    settlement.isAddSent = true;
    const addError = await addToCart(items).then(
      () => null,
      (error: unknown) => error,
    );
    if (!addError) settlement.acceptedQuantities = items.map(({ quantity }) => quantity);
    const fetchedCart = (await syncCart()) ?? (addError ? await syncCart() : null);
    if (!addError) {
      return {
        outcome: 'complete',
        addedQuantities: items.map(({ quantity }) => quantity),
        isStockRejection: false,
      };
    }
    console.error('Failed to add items to cart', addError);
    if (isStockRejectionError(addError)) refreshStockAfterRejection(items, releasedCart.items);
    return getFailedAddResult(items, addError, releasedCart, fetchedCart);
  };

  const runAdd = async (items: AddToCartItem[]): Promise<AddCartItemsResult> => {
    const addedLineIdentities = new Set(
      items.map(({ id, properties }) => getLineIdentity(id, properties)),
    );
    const { error, cartBefore, fetchedCart } = await mutateAndSync(
      async () => {
        await addToCart(items);
      },
      (item) => addedLineIdentities.has(getLineIdentity(item.variant_id, item.properties)),
    );
    if (!error) {
      return {
        outcome: 'complete',
        addedQuantities: items.map(({ quantity }) => quantity),
        isStockRejection: false,
      };
    }
    console.error('Failed to add items to cart', error);
    if (isStockRejectionError(error)) refreshStockAfterRejection(items, cartBefore?.items ?? []);
    return getFailedAddResult(items, error, cartBefore, fetchedCart);
  };

  const runActionPlan = async (
    items: AddToCartItem[],
    actionPlan: CartActionPlan,
  ): Promise<AddCartItemsResult> => {
    const cart = cartData.value;
    if (isCartStale || !areRemovalsCurrent(cart, actionPlan.removals)) {
      return getRejectedAddResult(items, false);
    }
    const fit = fitsProjectedCart(
      items,
      cart,
      actionPlan,
      getExactStock,
      findVariantParentMaxQuantityFamilies(),
    );
    if (fit !== 'fits') return getRejectedAddResult(items, fit === 'stock-short');
    const swap = planRemovalSwap(items, cart, actionPlan.removals, getExactStock);
    return swap ? runStockSwap(items, swap) : runAdd(items);
  };

  const runOrdinaryAdd = async (items: AddToCartItem[]): Promise<AddCartItemsResult> => {
    const stockSwap = isCartStale ? null : prepareStockSwap(items, cartData.value);
    return stockSwap ? runStockSwap(items, stockSwap) : runAdd(items);
  };

  const runStockLimitedAdd = async (items: AddToCartItem[]): Promise<AddCartItemsResult> => {
    const quantity = items.length === 1 ? getStockFittedAddQuantity(items[0]) : 0;
    if (quantity <= 0) {
      return getRejectedAddResult(items, true);
    }
    const result = await runOrdinaryAdd([{ ...items[0], quantity }]);
    if (result.outcome !== 'complete') return result;
    return { ...result, outcome: 'partial', isStockRejection: true };
  };

  const enqueueCartAdd = (
    items: AddToCartItem[],
    { actionPlan, redrawItems, freeGiftSku }: AddCartItemsOptions,
  ): Promise<AddCartItemsResult> => {
    dismissCartNotices();
    startCartAdd();
    return enqueueCartMutation(async () => {
      if (!(await prepareCartForMutation())) return getRejectedAddResult(items, false);
      if (freeGiftSku && !freeGiftItem.value) setChosenFreeGiftSku(freeGiftSku);
      const { stockCheck, items: checkedItems } = await checkAddStockWithRedraws(
        items,
        redrawItems,
      );
      if (stockCheck === 'not-refreshed') return getRejectedAddResult(items, false);
      if (stockCheck === 'unavailable' && actionPlan) return getRejectedAddResult(items, true);
      const runCheckedAdd = stockCheck === 'available' ? runOrdinaryAdd : runStockLimitedAdd;
      const result = actionPlan
        ? await runActionPlan(checkedItems, actionPlan)
        : await runCheckedAdd(checkedItems);
      if (result.outcome === 'partial' && result.isStockRejection) setCartAddLimitedByStock(true);
      const addedLineIdentities = checkedItems
        .filter((_, index) => result.addedQuantities[index] > 0)
        .map(({ id, properties }) => getLineIdentity(id, properties));
      if (addedLineIdentities.length) setLatestAddLineIdentities(addedLineIdentities);
      return result;
    });
  };

  const addCartItems = async (
    items: AddToCartItem[],
    options: AddCartItemsOptions = {},
  ): Promise<AddCartItemsResult> => {
    const result = await enqueueCartAdd(items, options);
    const isAdded = result.outcome === 'complete' || result.outcome === 'partial';
    if (options.freeGiftSku && isAdded && freeGiftItem.value) {
      await selectFreeGift(options.freeGiftSku);
    }
    return result;
  };

  const assertItemInCart = (key: string) => {
    if (!cartData.value.items.some((item) => key === item.key)) {
      throw new Error(`Item with key ${key} doesn't exist in cart`);
    }
  };

  const updateCartItemQuantities = async (
    input: CartItemQuantitiesInput,
    { isStockCorrection = false }: { isStockCorrection?: boolean } = {},
  ): Promise<ShopifyCart> => {
    let updatedKeys = new Set<string>();
    const { error } = await runCartMutation(
      async () => {
        const items = typeof input === 'function' ? input(cartData.value) : input;
        updatedKeys = new Set(items.map(({ key }) => key));
        if (!items.length) return;
        const updates: Record<string, number> = items.reduce((updateObject, { key, quantity }) => {
          assertItemInCart(key);
          updateObject[key] = quantity;
          return updateObject;
        }, {});
        if (isStockCorrection) {
          trackStockCorrection(getReducedLineQuantities(cartData.value, items));
        }
        return updateCart(updates);
      },
      (item) => updatedKeys.has(item.key),
    );
    if (error) console.error(error);
    return cartData.value;
  };

  const changeCartItemQuantity = async ({
    key,
    quantity,
  }: CartItemQuantity): Promise<CartItemChangeResult> => {
    dismissCartNotices();
    const { error, cartBefore, fetchedCart } = await runCartMutation(
      async () => {
        assertItemInCart(key);
        await changeCartItem(key, quantity);
      },
      (item) => item.key === key,
    );
    if (error) console.error(error);
    const isStockRejection = isStockRejectionError(error);
    if (isStockRejection) {
      const line = cartBefore?.items.find((item) => item.key === key);
      refreshStockAfterRejection(
        line ? [{ id: line.variant_id, quantity, properties: line.properties }] : [],
        cartBefore?.items.filter((item) => item.key !== key) ?? [],
      );
    }
    const getLineQuantity = (cart: ShopifyCart) =>
      cart.items.find((item) => item.key === key)?.quantity ?? 0;
    return {
      error,
      isStockRejection,
      isConfirmed: !!fetchedCart,
      isLimitedByCartRules:
        !!fetchedCart &&
        getLineQuantity(fetchedCart) >= quantity &&
        getLineQuantity(cartData.value) < quantity,
    };
  };

  const removeCartItems = async (items: string[]): Promise<ShopifyCart> => {
    dismissCartNotices();
    const formattedItems: CartItemQuantity[] = items.map((key) => ({ key, quantity: 0 }));
    return updateCartItemQuantities(formattedItems);
  };

  const emptyCart = async (): Promise<ShopifyCart> => {
    const { error } = await runCartMutation(clearCart, () => true);
    if (error) console.error(error);
    return cartData.value;
  };

  const setCartAttributes = async (attributes: Record<string, string>): Promise<boolean> => {
    const { error } = await runCartMutation(
      () => updateCartAttributes(attributes),
      () => false,
    );
    if (error) console.error('Failed to update cart attributes', error);
    return !error;
  };

  return {
    refreshCart,
    addCartItems,
    updateCartItemQuantities,
    changeCartItemQuantity,
    addCartUrlItems: urlActionFlow.addCartUrlItems,
    removeCartItems,
    emptyCart,
    setCartAttributes,
    selectFreeGift,
  };
};
